// packages/web/src/components/Chat/ChatView.tsx - Mobile Responsive Version

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Place as PlaceIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { Chat, Message, ChatService } from '@matchpoint/shared';
import { format, isSameDay, isToday, isTomorrow, isYesterday } from 'date-fns';
import { MessageInput } from './MessageInput';

interface ChatViewProps {
  chat: Chat;
  currentUserId: string;
  currentUserName: string;
  onBack?: () => void; // Optional back handler for mobile
}

export const ChatView: React.FC<ChatViewProps> = ({
  chat,
  currentUserId,
  currentUserName,
  onBack
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const chatService = ChatService.getInstance();

  const loadMessages = async () => {
    try {
      setLoading(true);
      setError(null);
      const chatMessages = await chatService.getMessages(chat.id);
      setMessages(chatMessages);
    } catch (err) {
      console.error('Error loading messages:', err);
      setError('Failed to load messages. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
  }, [chat.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (content: string) => {
    try {
      setSending(true);
      setError(null);
      
      await chatService.sendMessage(
        chat.id,
        currentUserId,
        currentUserName,
        content
      );
      
      // Reload messages to get the new one
      await loadMessages();
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getOtherParticipant = () => {
    const currentUserIndex = chat.participants.indexOf(currentUserId);
    if (currentUserIndex === -1) return { name: 'Unknown', id: '' };
    
    const otherUserIndex = currentUserIndex === 0 ? 1 : 0;
    return {
      id: chat.participants[otherUserIndex],
      name: chat.participantNames[otherUserIndex]
    };
  };

  const formatMatchDate = (date: Date) => {
    if (isToday(date)) {
      return `Today at ${format(date, 'h:mm a')}`;
    } else if (isTomorrow(date)) {
      return `Tomorrow at ${format(date, 'h:mm a')}`;
    } else if (isYesterday(date)) {
      return `Yesterday at ${format(date, 'h:mm a')}`;
    } else {
      return format(date, 'EEEE, MMMM d \'at\' h:mm a');
    }
  };

  const formatMessageTime = (date: Date) => {
    return format(date, 'h:mm a');
  };

  const isChatActive = () => {
    return new Date() <= chat.expiresAt;
  };

  const getTimeUntilExpiration = (): string => {
    const now = new Date();
    const msUntilExpiration = chat.expiresAt.getTime() - now.getTime();
    
    if (msUntilExpiration <= 0) {
      return 'expired';
    }
    
    const hoursUntilExpiration = Math.floor(msUntilExpiration / (1000 * 60 * 60));
    const minutesUntilExpiration = Math.floor((msUntilExpiration % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hoursUntilExpiration > 0) {
      return `${hoursUntilExpiration}h ${minutesUntilExpiration}m`;
    } else {
      return `${minutesUntilExpiration}m`;
    }
  };

  const otherParticipant = getOtherParticipant();
  const isActive = isChatActive();
  const timeUntilExpiration = getTimeUntilExpiration();

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Chat Header */}
      <Paper sx={{ 
        p: { xs: 0.5, sm: 2 }, 
        borderRadius: 0, 
        borderBottom: '1px solid', 
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.5, sm: 0 },
        minHeight: { xs: 'auto', sm: 'auto' }
      }}>
        {/* Back button for mobile */}
        {isMobile && onBack && (
          <IconButton 
            onClick={onBack}
            size="small"
            sx={{ 
              color: 'text.primary',
              p: 0.5
            }}
          >
            <ArrowBackIcon sx={{ fontSize: '1.2rem' }} />
          </IconButton>
        )}
        
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ 
            fontSize: { xs: '0.85rem', sm: '1.25rem' },
            fontWeight: 600,
            mb: 0
          }}>
            {otherParticipant.name}
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row',
            gap: { xs: 0.5, sm: 1 },
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <ScheduleIcon sx={{ fontSize: { xs: 11, sm: 16 }, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.875rem' } }}>
                {formatMatchDate(chat.matchTime)}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <PlaceIcon sx={{ fontSize: { xs: 11, sm: 16 }, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" noWrap sx={{ 
                fontSize: { xs: '0.65rem', sm: '0.875rem' },
                maxWidth: { xs: '120px', sm: 'none' }
              }}>
                {chat.courtName}
              </Typography>
            </Box>
            
            {isActive ? (
              <Typography variant="caption" color="primary.main" sx={{ 
                fontWeight: 'medium', 
                fontSize: { xs: '0.6rem', sm: '0.75rem' }
              }}>
                Expires in {timeUntilExpiration}
              </Typography>
            ) : (
              <Typography variant="caption" color="error.main" sx={{ 
                fontWeight: 'medium', 
                fontSize: { xs: '0.6rem', sm: '0.75rem' }
              }}>
                Expired
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Messages Area */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto', 
        p: { xs: 0.75, sm: 1 },
        display: 'flex',
        flexDirection: 'column'
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {error}
          </Alert>
        ) : messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: { xs: 2, sm: 3 }, color: 'text.secondary' }}>
            <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              No messages yet
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              {isActive ? 'Start the conversation!' : 'This chat has expired.'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.5, sm: 1 } }}>
            {messages.map((message, index) => {
              const isOwn = message.senderId === currentUserId;
              const showDate = index === 0 || !isSameDay(message.createdAt, messages[index - 1].createdAt);
              
              return (
                <React.Fragment key={message.id}>
                  {showDate && (
                    <Box sx={{ textAlign: 'center', my: { xs: 0.5, sm: 1 } }}>
                      <Typography variant="caption" color="text.secondary" sx={{ 
                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                        backgroundColor: 'rgba(0,0,0,0.05)',
                        padding: { xs: '2px 8px', sm: '4px 12px' },
                        borderRadius: '12px',
                        display: 'inline-block'
                      }}>
                        {format(message.createdAt, 'MMMM d, yyyy')}
                      </Typography>
                    </Box>
                  )}
                  
                  <Box sx={{
                    display: 'flex',
                    justifyContent: isOwn ? 'flex-end' : 'flex-start',
                    mb: { xs: 0.25, sm: 0.5 }
                  }}>
                    <Paper sx={{
                      p: { xs: 0.75, sm: 1.5 },
                      maxWidth: { xs: '80%', sm: '70%' },
                      bgcolor: isOwn ? 'primary.main' : 'grey.100',
                      color: isOwn ? 'primary.contrastText' : 'text.primary',
                      borderRadius: { xs: '12px', sm: '16px' },
                      boxShadow: 'none'
                    }}>
                      <Typography variant="body2" sx={{ 
                        fontSize: { xs: '0.8rem', sm: '1rem' },
                        wordBreak: 'break-word'
                      }}>
                        {message.content}
                      </Typography>
                      <Typography variant="caption" sx={{
                        display: 'block',
                        mt: { xs: 0.25, sm: 0.5 },
                        opacity: 0.7,
                        fontSize: { xs: '0.6rem', sm: '0.7rem' }
                      }}>
                        {formatMessageTime(message.createdAt)}
                      </Typography>
                    </Paper>
                  </Box>
                </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </Box>
        )}
      </Box>

      {/* Message Input */}
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
        {!isActive && (
          <Alert severity="info" sx={{ 
            borderRadius: 0, 
            fontSize: { xs: '0.7rem', sm: '0.875rem' },
            py: { xs: 0.5, sm: 1 }
          }}>
            This chat has expired. Messages can only be sent up to 48 hours after the match time.
          </Alert>
        )}
        
        <MessageInput
          onSendMessage={handleSendMessage}
          disabled={!isActive || sending}
          loading={sending}
        />
      </Box>
    </Box>
  );
};